import usePagination from "src/customHooks/usePagination";
import PageHeader from "src/components/pageHeader";
import Pagination from "src/components/pagination";
import PageLoading from "src/components/pageLoading";
import { Slot } from "../types/slot.entity";
import { useCountSlotsQuery, useGetSlotsQuery, SlotsFilter } from "../slots.service";
import SlotsTable from "../components/slots-table";
import { SlotsSearch } from "../components/slots-search";
import { SlotsTableSkeleton } from "../components/SlotsTableSkeleton";
import { useCallback } from "react";
import { usePaginatedTableSearchParams } from "src/customHooks/usePaginatedTableSearchParams";

const SlotsPage = () => {
  const tableSearch = usePaginatedTableSearchParams<SlotsFilter>({
    parseState: (searchParams) => {
      const query: SlotsFilter = {};
      const userId = searchParams.get("userId");
      const slotId = searchParams.get("slotId");
      const status = searchParams.get("status");
      const coachType = searchParams.get("coachType");
      const langCode = searchParams.get("langCode");

      if (userId) query.userId = userId;
      if (slotId) query.slotId = slotId;
      if (status) query.status = status as SlotsFilter["status"];
      if (coachType) query.coachType = coachType as SlotsFilter["coachType"];
      if (langCode) query.langCode = langCode as SlotsFilter["langCode"];

      return query;
    },
    serializeState: (state) => {
      const nextParams = new URLSearchParams();
      if (state.userId) nextParams.set("userId", state.userId);
      if (state.slotId) nextParams.set("slotId", state.slotId);
      if (state.status) nextParams.set("status", state.status);
      if (state.coachType) nextParams.set("coachType", state.coachType);
      if (state.langCode) nextParams.set("langCode", state.langCode);
      return nextParams;
    },
  });

  const initialQuery = tableSearch.state;

  const { data, numberOfPages, currentPage, next, prev, isSuccess, isError, isLoading, isFetching } =
    usePagination<Slot & { id: string }>({
      useGetDataQuery: useGetSlotsQuery,
      useCountData: useCountSlotsQuery,
      initQuery: initialQuery,
      initPageTokens: tableSearch.pageTokens,
      initCurrentIndex: tableSearch.currentIndex,
      syncKey: tableSearch.syncKey,
      onPaginationStateChange: tableSearch.replacePagination,
    });

  const updateQuery = useCallback((newQuery: SlotsFilter) => {
    tableSearch.replaceFilters(newQuery);
  }, [tableSearch]);

  const showTableSkeleton = !data && (isLoading || isFetching);

  return (
    <div className="page_container">
      <PageHeader text="Slots" />

      <div className="mb-4">
        <SlotsSearch updateQuery={updateQuery} initialQuery={initialQuery} />
      </div>

      {isSuccess ? (
        <>
          {showTableSkeleton ? (
            <div className="overflow-x-auto mb-6">
              <table className="min-w-full border border-gray-200 divide-y divide-gray-200">
                <thead className="bg-gray-900 text-white text-center">
                  <tr>
                    <th className="px-4 py-3 uppercase text-xs tracking-wider">Slot</th>
                    <th className="px-4 py-3 uppercase text-xs tracking-wider">User</th>
                    <th className="px-4 py-3 uppercase text-xs tracking-wider">Lang</th>
                    <th className="px-4 py-3 uppercase text-xs tracking-wider">Status</th>
                    <th className="px-4 py-3 uppercase text-xs tracking-wider">Coach</th>
                    <th className="px-4 py-3 uppercase text-xs tracking-wider">Created</th>
                    <th className="px-4 py-3 uppercase text-xs tracking-wider">When</th>
                    <th className="px-4 py-3 uppercase text-xs tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <SlotsTableSkeleton rows={10} />
                </tbody>
              </table>
            </div>
          ) : data ? (
            <SlotsTable data={data} />
          ) : (
            <SlotsTable data={[]} />
          )}
          <Pagination numberOfPages={numberOfPages || 1} currentPage={currentPage} prev={prev} next={next} isLoading={isLoading || isFetching} />
        </>
      ) : isError ? (
        <div className="mt-6 p-4 rounded border border-red-200 bg-red-50 text-red-700">
          Something went wrong loading slots. Please adjust your filters or try again.
        </div>
      ) : (
        <PageLoading />
      )}
    </div>
  );
};

export default SlotsPage;
